import React, { createContext, useContext, useState, useEffect } from 'react';
import supabase from '../supabase/supabase';

const PaymentContext = createContext();

export const usePayment = () => {
  const context = useContext(PaymentContext);
  if (!context) {
    throw new Error('usePayment must be used within a PaymentProvider');
  }
  return context;
};

export const PaymentProvider = ({ children }) => {
  const [paymentSettings, setPaymentSettings] = useState({
    stripePublishableKey: '',
    stripeSecretKey: '',
    currency: 'USD',
    taxRate: 0.08,
    processingFee: 0.029,
    enabled: false
  });

  useEffect(() => {
    loadPaymentSettings();
  }, []);

  const loadPaymentSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('payment_settings')
        .select('*')
        .limit(1)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      
      if (data) {
        setPaymentSettings({
          stripePublishableKey: data.stripe_publishable_key || '',
          stripeSecretKey: data.stripe_secret_key || '',
          currency: data.currency || 'USD',
          taxRate: parseFloat(data.tax_rate) || 0.08,
          processingFee: parseFloat(data.processing_fee) || 0.029,
          enabled: data.enabled || false
        });
      }
    } catch (error) {
      console.error('Error loading payment settings:', error);
    }
  };

  const updatePaymentSettings = async (settings) => {
    try {
      const updateData = {
        stripe_publishable_key: settings.stripePublishableKey,
        stripe_secret_key: settings.stripeSecretKey,
        currency: settings.currency,
        tax_rate: settings.taxRate,
        processing_fee: settings.processingFee,
        enabled: settings.enabled,
        updated_at: new Date().toISOString()
      };

      // Check if settings exist
      const { data: existing } = await supabase
        .from('payment_settings')
        .select('id')
        .limit(1)
        .single();

      if (existing) {
        const { error } = await supabase
          .from('payment_settings')
          .update(updateData)
          .eq('id', existing.id);
        
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('payment_settings')
          .insert([updateData]);
        
        if (error) throw error;
      }

      setPaymentSettings({
        stripePublishableKey: settings.stripePublishableKey,
        stripeSecretKey: settings.stripeSecretKey,
        currency: settings.currency,
        taxRate: settings.taxRate,
        processingFee: settings.processingFee,
        enabled: settings.enabled
      });
    } catch (error) {
      console.error('Error updating payment settings:', error);
    }
  };

  const calculateTax = (subtotal) => {
    return subtotal * paymentSettings.taxRate;
  };

  const calculateProcessingFee = (subtotal) => {
    return subtotal * paymentSettings.processingFee;
  };

  const calculateTotal = (subtotal) => {
    const tax = calculateTax(subtotal);
    const processingFee = calculateProcessingFee(subtotal);
    return subtotal + tax + processingFee;
  };

  const processPayment = async (paymentData) => {
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // Mock successful payment
    return {
      success: true,
      transactionId: `txn_${Date.now()}`,
      message: 'Payment processed successfully'
    };
  };

  return (
    <PaymentContext.Provider value={{
      paymentSettings,
      updatePaymentSettings,
      calculateTax,
      calculateProcessingFee,
      calculateTotal,
      processPayment
    }}>
      {children}
    </PaymentContext.Provider>
  );
};